import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import "./mediaLibrary.css";
import {
  formatMediaDateTime,
  formatMediaSizeLabel,
  hasMediaStorageLimit,
  type MediaImportResult,
  MEDIA_UPLOAD_MAX_BYTES,
  MEDIA_UPLOAD_MAX_DIMENSION_PX,
  MEDIA_UPLOAD_RECOMMENDED_BYTES,
  importMediaFile,
  listMediaLibrary,
  recordMediaUsage,
  type MediaLibraryItem,
  type MediaUsagePayload,
} from "./mediaClient";

type MediaLibraryModalProps = {
  opened: boolean;
  title?: string;
  subtitle?: string;
  confirmLabel?: string;
  mode?: "full" | "import-only";
  usage?: MediaUsagePayload;
  onClose: () => void;
  onConfirm?: (item: MediaLibraryItem) => void;
  onImportComplete?: (result: MediaImportResult) => void | Promise<void>;
};

type ModalTab = "import" | "library";

function deriveInspectorTitle(item?: MediaLibraryItem | null) {
  if (!item) {
    return "Aucun média sélectionné";
  }
  return item.original_name || item.stored_name;
}

export function MediaLibraryModal({
  opened,
  title = "Bibliothèque médias",
  subtitle = "Importez une image depuis votre ordinateur ou sélectionnez un média existant.",
  confirmLabel = "Utiliser cette image",
  mode = "full",
  usage,
  onClose,
  onConfirm,
  onImportComplete,
}: MediaLibraryModalProps) {
  const queryClient = useQueryClient();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const isImportOnly = mode === "import-only";
  const [activeTab, setActiveTab] = useState<ModalTab>(isImportOnly ? "import" : "library");
  const [selectedMediaId, setSelectedMediaId] = useState<number | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>("");

  const mediaQuery = useQuery({
    queryKey: ["media-library-shared"],
    queryFn: listMediaLibrary,
    enabled: opened,
  });

  const items = mediaQuery.data?.items || [];
  const stats = mediaQuery.data?.stats;
  const quota = stats?.quota;
  const hasStorageLimit = hasMediaStorageLimit(quota);
  const usagePercent = Math.min(100, Math.max(0, quota?.usage_percent || 0));
  const isImportTab = activeTab === "import";
  const isLibraryTab = activeTab === "library";
  const isEmbeddedImportTab = !isImportOnly && isImportTab;
  const showLibraryInspector = !isImportOnly && isLibraryTab;
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedMediaId) || items[0] || null,
    [items, selectedMediaId]
  );

  useEffect(() => {
    if (!opened) {
      return;
    }
    if (isImportOnly) {
      setActiveTab("import");
    }
    if (items.length === 0) {
      setSelectedMediaId(null);
      return;
    }
    if (!selectedMediaId || !items.some((item) => item.id === selectedMediaId)) {
      setSelectedMediaId(items[0].id);
    }
  }, [isImportOnly, items, opened, selectedMediaId]);

  useEffect(() => {
    if (!opened) {
      setActiveTab(isImportOnly ? "import" : "library");
      setUploadStatus("");
    }
  }, [opened, isImportOnly]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [opened, onClose]);

  const importMutation = useMutation({
    mutationFn: async (file: File) => importMediaFile(file),
    onSuccess: async (result) => {
      setUploadStatus(result.message);
      await queryClient.invalidateQueries({ queryKey: ["media-library-shared"] });
      if (isImportOnly) {
        await onImportComplete?.(result);
        onClose();
        return;
      }
      setActiveTab("library");
      setSelectedMediaId(result.item.id);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Le fichier n'a pas pu être importé.";
      setUploadStatus(message);
    },
  });

  const usageMutation = useMutation({
    mutationFn: async (item: MediaLibraryItem) => {
      if (!usage) {
        return item;
      }
      const result = await recordMediaUsage(item.id, usage);
      return result.item;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["media-library-shared"] });
    },
  });

  async function handleConfirm() {
    if (!selectedItem || !onConfirm) {
      return;
    }
    const trackedItem = await usageMutation.mutateAsync(selectedItem);
    onConfirm(trackedItem);
    onClose();
  }

  const importPanel = (
    <div className="easy-media-import-layout">
      <div className="easy-media-import-summary">
        {stats ? (
          <div className="easy-media-import-storage-inline">
            <div className="easy-media-import-storage-copy">
              <span className="section-kicker">Stockage</span>
              <strong>{hasStorageLimit ? `${quota?.used_label || "-"} / ${quota?.limit_label || "-"}` : quota?.used_label || "-"}</strong>
            </div>
            <div className="easy-media-import-storage-side">
              <div className={`easy-media-storage-pill is-${quota?.status || "ok"}`}>
                {hasStorageLimit ? `${usagePercent}%` : "Sans limite"}
              </div>
              <div
                className={`easy-media-import-storage-bar is-${quota?.status || "ok"}`}
                role={hasStorageLimit ? "progressbar" : undefined}
                aria-label="Utilisation du stockage média"
                aria-valuemin={hasStorageLimit ? 0 : undefined}
                aria-valuemax={hasStorageLimit ? 100 : undefined}
                aria-valuenow={hasStorageLimit ? usagePercent : undefined}
              >
                <span style={{ width: `${hasStorageLimit ? usagePercent : 100}%` }} />
              </div>
            </div>
          </div>
        ) : null}

        <div className="easy-media-import-rules">
          <span className="easy-media-chip">{formatMediaSizeLabel(MEDIA_UPLOAD_MAX_BYTES)} max à l’import</span>
          <span className="easy-media-chip">{MEDIA_UPLOAD_MAX_DIMENSION_PX} px max</span>
          <span className="easy-media-chip">{formatMediaSizeLabel(MEDIA_UPLOAD_RECOMMENDED_BYTES)} recommandé</span>
          <span className="easy-media-chip">JPEG PNG WebP GIF AVIF BMP TIFF HEIC</span>
        </div>
      </div>

      <div className="easy-media-upload-zone">
        <div className="easy-media-upload-zone-copy">
          <strong>Ajouter un fichier image</strong>
          <p className="easy-media-muted">
            Compression automatique en JPEG ou PNG, jusqu’à 3 Mo et 2400 px. La transparence est conservée ; les animations deviennent des images fixes. Les SVG ne sont pas acceptés.
          </p>
        </div>
        <div className="easy-media-upload-zone-actions">
          <button
            type="button"
            className="button button-primary"
            disabled={importMutation.isPending}
            onClick={() => uploadInputRef.current?.click()}
          >
            {importMutation.isPending ? "Import en cours..." : "Choisir un fichier"}
          </button>
          <p className="easy-media-upload-caption">L'image importée sera sélectionnée automatiquement dans l'onglet Médias.</p>
        </div>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,image/tiff,image/heic,image/heif,image/jp2,image/x-icon,.jpg,.jpeg,.png,.webp,.gif,.avif,.bmp,.tif,.tiff,.heic,.heif,.jp2,.ico"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) {
              return;
            }
            importMutation.mutate(file);
            event.target.value = "";
          }}
        />
      </div>
    </div>
  );

  if (!opened) {
    return null;
  }

  return (
    <div className="easy-media-modal-backdrop" onClick={onClose}>
      <div
        className={`easy-media-modal${isImportOnly ? " is-import-only" : ""}${isEmbeddedImportTab ? " is-import-stage" : ""}`}
        role="dialog"
        aria-modal={true}
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="easy-media-modal-head">
          <div>
            <p className="section-kicker">{isImportOnly ? "Import" : "Médias"}</p>
            <h2>{title}</h2>
            <p className="muted">{subtitle}</p>
          </div>
          <button type="button" className="button" onClick={onClose}>
            Fermer
          </button>
        </div>

        {!isImportOnly ? (
          <div className="easy-media-modal-main">
            <div className="easy-media-modal-tabs" role="tablist" aria-label="Choix de l'onglet">
              <button
                type="button"
                className={`easy-media-modal-tab${activeTab === "import" ? " is-active" : ""}`}
                aria-selected={activeTab === "import"}
                onClick={() => setActiveTab("import")}
              >
                Importer
              </button>
              <button
                type="button"
                className={`easy-media-modal-tab${activeTab === "library" ? " is-active" : ""}`}
                aria-selected={activeTab === "library"}
                onClick={() => setActiveTab("library")}
              >
                Médias
              </button>
            </div>
          </div>
        ) : null}

        <div className={`easy-media-modal-body${isImportOnly || !showLibraryInspector ? " is-import-only" : ""}`}>
          <div className="easy-media-modal-main">
            {uploadStatus ? <p className="status-line">{uploadStatus}</p> : null}
            {activeTab === "import" ? importPanel : mediaQuery.isLoading ? (
              <div className="easy-media-empty">Chargement de la médiathèque...</div>
            ) : mediaQuery.error instanceof Error ? (
              <div className="easy-media-error">
                <strong>Bibliotheque indisponible</strong>
                <p>{mediaQuery.error.message}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="easy-media-empty">
                <strong>Aucune image pour l'instant</strong>
                <p>Importez votre premiere image depuis l'onglet Importer.</p>
              </div>
            ) : (
              <div className="easy-media-grid">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`easy-media-card${selectedItem?.id === item.id ? " is-selected" : ""}`}
                    onClick={() => setSelectedMediaId(item.id)}
                  >
                    <div className="easy-media-card-preview">
                      <img src={item.public_url} alt={item.alt_text || item.original_name} />
                    </div>
                    <div className="easy-media-card-copy">
                      <strong>{item.original_name}</strong>
                      <span>{item.size_label}</span>
                      <small>{item.dimensions_label || item.mime_type}</small>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {showLibraryInspector ? (
            <aside className="easy-media-inspector">
              <div className="easy-media-inspector-preview">
                {selectedItem ? <img src={selectedItem.public_url} alt={selectedItem.alt_text || selectedItem.original_name} /> : null}
              </div>
              <div>
                <p className="section-kicker">Sélection</p>
                <h3>{deriveInspectorTitle(selectedItem)}</h3>
              </div>
              <div className="easy-media-meta-list">
                <div>
                  <span>Nom</span>
                  <strong>{selectedItem?.original_name || "-"}</strong>
                </div>
                <div>
                  <span>Date d'ajout</span>
                  <strong>{formatMediaDateTime(selectedItem?.created_at)}</strong>
                </div>
                <div>
                  <span>Taille</span>
                  <strong>{selectedItem?.size_label || "-"}</strong>
                </div>
                <div>
                  <span>Dimensions</span>
                  <strong>{selectedItem?.dimensions_label || "-"}</strong>
                </div>
                <div>
                  <span>Dernière utilisation</span>
                  <strong>{formatMediaDateTime(selectedItem?.last_used_at)}</strong>
                </div>
              </div>
              {selectedItem ? (
                <div className="easy-media-chip-row">
                  <span className="easy-media-chip">{selectedItem.mime_type}</span>
                  <span className="easy-media-chip">{selectedItem.usage_count} utilisation(s)</span>
                </div>
              ) : null}
            </aside>
          ) : null}
        </div>

        {!isImportOnly && isLibraryTab ? (
          <div className="easy-media-modal-footer">
            <p className="easy-media-muted">Validation: l'image sélectionnée est insérée dans l'écran en cours.</p>
            <div className="easy-media-modal-footer-actions">
              <button type="button" className="button" onClick={onClose}>
                Annuler
              </button>
              <button
                type="button"
                className="button button-primary"
                disabled={!selectedItem || usageMutation.isPending || !onConfirm}
                onClick={() => void handleConfirm()}
              >
                {usageMutation.isPending ? "Insertion..." : confirmLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
